import { Eye, Pencil, Trash2 } from "lucide-react";
import { InputSelect } from "@/shared/components/form/InputSelect";
import type { OrganizationTableRow } from "../types/organizations-api.types";
import {
  getOrganizationStatusStyles,
  getProviderStyles,
} from "../utils/organizationHelpers";
import { OrganizationStatus, Provider } from "../types/organizations-api.types";
import useTranslations from "@/shared/lang/useTranslations";

type SelectOption = {
  label: string;
  value: string;
};

type OrganizationsTableProps = {
  organizations: OrganizationTableRow[];
  totalItems: number;
  itemsPerPage: number;
  paginationOptions: SelectOption[];
  onItemsPerPageChange: (value: string) => void;
  onViewOrganizationDetail?: (organizationId: string) => void;
  onUpdateOrganization?: (organizationId: string) => void;
  onDeleteOrganization?: (organizationId: string) => void;
};

export const OrganizationsTable = ({
  organizations,
  totalItems,
  itemsPerPage,
  paginationOptions,
  onItemsPerPageChange,
  onViewOrganizationDetail,
  onUpdateOrganization,
  onDeleteOrganization,
}: OrganizationsTableProps) => {
  const { t } = useTranslations("cards");

  const getStatusLabel = (status: OrganizationStatus): string => {
    const statusMap: Record<OrganizationStatus, string> = {
      [OrganizationStatus.ACTIVE]: t("organizations_status_active"),
      [OrganizationStatus.INACTIVE]: t("organizations_status_inactive"),
      [OrganizationStatus.SUSPENDED]: t("organizations_status_suspended"),
      [OrganizationStatus.PENDING]: t("organizations_status_pending"),
    };
    return statusMap[status] || status;
  };

  const getProviderLabel = (provider: Provider | null): string => {
    if (!provider) return t("organizations_provider_na");
    const providerMap: Record<Provider, string> = {
      [Provider.BLOQUE]: t("organizations_provider_bloque"),
      [Provider.CRYPTO_MATE]: t("organizations_provider_crypto_mate"),
    };
    return providerMap[provider] || provider;
  };

  return (
    <>
      <div className="px-6 py-4 border-b border-gray-200 dark:border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t("organizations_table_showing")} {organizations.length}{" "}
            {organizations.length === 1 ? t("organizations_table_showing_singular") : t("organizations_table_showing_plural")}{" "}
            {totalItems > 0 && `(${t("table_showing_total")}: ${totalItems})`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">{t("organizations_table_show")}</span>
          <InputSelect<SelectOption>
            options={paginationOptions}
            value={
              paginationOptions.find((opt) => opt.value === String(itemsPerPage)) ||
              null
            }
            onChange={(selected) => {
              const value = Array.isArray(selected)
                ? selected[0]?.value
                : selected?.value;
              onItemsPerPageChange(value || "10");
            }}
            maxWidth="max-w-[120px]"
            small
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-zinc-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("organizations_table_header_name")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("organizations_table_header_email")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("organizations_table_header_phone")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("organizations_table_header_status")}
              </th>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("organizations_table_header_card_provider")}
              </th>
              <th className="px-12 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t("organizations_table_header_actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-zinc-900 divide-y divide-gray-200 dark:divide-zinc-800">
            {organizations.map((organization) => (
              <tr
                key={organization.id}
                className="hover:bg-gray-50 dark:hover:bg-zinc-800/50"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  {organization.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {organization.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {organization.phone || t("organizations_provider_na")}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={getOrganizationStatusStyles(organization.status)}>
                    {getStatusLabel(organization.status)}
                  </span>
                </td>
                <td className="px-2 py-4 whitespace-nowrap">
                  <span className={getProviderStyles(organization.card_provider)}>
                    {getProviderLabel(organization.card_provider)}
                  </span>
                </td>
                <td className="px-12 py-4 whitespace-nowrap">
                  <div className="flex items-center justify-center gap-2">
                    <div className="relative group">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-full p-2 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-100 dark:hover:bg-emerald-500/25 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                        aria-label={t("organizations_table_tooltip_view_detail")}
                        onClick={() => onViewOrganizationDetail?.(organization.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs font-medium text-white bg-black dark:bg-zinc-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                        {t("organizations_table_tooltip_view_detail_en")}
                      </span>
                    </div>
                    <div className="relative group">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-full p-2 border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-blue-100 dark:hover:bg-blue-500/25 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                        aria-label={t("organizations_table_tooltip_edit")}
                        onClick={() => onUpdateOrganization?.(organization.id)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs font-medium text-white bg-black dark:bg-zinc-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                        {t("organizations_table_tooltip_edit_en")}
                      </span>
                    </div>
                    <div className="relative group">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-full p-2 border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                        aria-label={t("organizations_table_tooltip_delete")}
                        onClick={() => onDeleteOrganization?.(organization.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs font-medium text-white bg-black dark:bg-zinc-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                        {t("organizations_table_tooltip_delete_en")}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
